import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { BRAND, SERVICES } from "../../data";
import { Reveal } from "./primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error("Please add your name, phone, and the service you need.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Request sent. We'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Call us at " + BRAND.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="relative py-24 md:py-36 px-6 md:px-12 lg:px-16 bg-brand-asphalt border-t border-brand-concrete">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <Reveal>
          <span className="font-mono-p text-xs uppercase tracking-[0.28em] text-brand-red">Get Started</span>
          <h2 className="mt-4 font-anton uppercase text-5xl md:text-7xl tracking-tight leading-[0.88]">
            Request<br />a Free<br /><span className="text-brand-red">Quote.</span>
          </h2>
          <p className="mt-8 font-ibm text-white/60 max-w-md leading-relaxed">
            Tell us about your site. We'll get back to you fast with a straight, honest quote —
            no runaround, no surprises.
          </p>

          <div className="mt-10 space-y-5">
            <a data-testid="contact-phone" href={BRAND.phoneHref} className="flex items-center gap-4 group">
              <span className="w-11 h-11 grid place-items-center bg-brand-red"><Phone className="w-5 h-5" /></span>
              <span className="font-anton text-2xl md:text-3xl tracking-wide group-hover:text-brand-red transition-colors">{BRAND.phone}</span>
            </a>
            <a data-testid="contact-email" href={`mailto:${BRAND.email}`} className="flex items-center gap-4 group">
              <span className="w-11 h-11 grid place-items-center bg-brand-concrete group-hover:bg-brand-red transition-colors"><Mail className="w-5 h-5" /></span>
              <span className="font-ibm text-sm md:text-base text-white/80 group-hover:text-brand-red transition-colors break-all">{BRAND.email}</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 grid place-items-center bg-brand-concrete"><MapPin className="w-5 h-5" /></span>
              <span className="font-ibm text-sm md:text-base text-white/80">{BRAND.area}</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form data-testid="lead-form" onSubmit={submit} className="bg-brand-black border border-brand-concrete p-6 md:p-10 clip-corner">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="font-mono-p text-xs uppercase tracking-[0.2em] text-white/50">Name *</label>
                <input data-testid="lead-name" className="field mt-2" placeholder="John Marquez" value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label className="font-mono-p text-xs uppercase tracking-[0.2em] text-white/50">Phone *</label>
                <input data-testid="lead-phone" className="field mt-2" placeholder="(239) 000-0000" value={form.phone} onChange={set("phone")} />
              </div>
            </div>

            <div className="mt-6">
              <label className="font-mono-p text-xs uppercase tracking-[0.2em] text-white/50">Email</label>
              <input data-testid="lead-email" type="email" className="field mt-2" placeholder="you@email.com" value={form.email} onChange={set("email")} />
            </div>

            <div className="mt-6">
              <label className="font-mono-p text-xs uppercase tracking-[0.2em] text-white/50">Service Needed *</label>
              <select data-testid="lead-service" className="field mt-2 appearance-none cursor-pointer" value={form.service} onChange={set("service")}>
                <option value="" className="bg-brand-black">Select a service…</option>
                {SERVICES.map((s) => (
                  <option key={s.n} value={s.title} className="bg-brand-black">{s.title}</option>
                ))}
                <option value="Other / Multiple" className="bg-brand-black">Other / Multiple</option>
              </select>
            </div>

            <div className="mt-6">
              <label className="font-mono-p text-xs uppercase tracking-[0.2em] text-white/50">Project Details</label>
              <textarea data-testid="lead-message" rows={4} className="field mt-2 resize-none" placeholder="Lot size, location, timeline…" value={form.message} onChange={set("message")} />
            </div>

            <button data-testid="lead-submit" type="submit" disabled={loading} className="btn-primary mt-8 w-full justify-center disabled:opacity-60">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Sending…</span></> : <span>Send Request</span>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
